import type { Language } from '@offer-catalog/services/i18n'
import type { ProductCardProps } from './types'
import { VpDivider } from '@vtmn-play/react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { NewSeasonBadge } from '@/components/NewSeasonBadge/NewSeasonBadge'
import { DataFallback } from '@/components/ProductCard/Parts/DataFallback'
// import { useSearchParams } from 'react-router'
import { ModelBasicInfo } from '@/components/ProductCard/Parts/ModelBasicInfo'
import { ModelCompoVariations } from '@/components/ProductCard/Parts/ModelCompoVariations'
import { ProductAvailability } from '@/components/ProductCard/Parts/ProductAvailabillity'
import { ProductBasicInfo } from '@/components/ProductCard/Parts/ProductBasicInfo'
import { ProductBenefits } from '@/components/ProductCard/Parts/ProductBenefits'
import { ProductCompoVariations } from '@/components/ProductCard/Parts/ProductCompoVariations'
import { ProductManufacture } from '@/components/ProductCard/Parts/ProductManufacture'
import { ProductImage } from '@/components/ProductImage/ProductImage'
// import { ProductFigures } from './Parts/ProductFigures'
import { ProductSustainability } from './Parts/ProductSustainability'
import { computeProductCardVisibility } from './productCardVisibility'
import './product-card.scss'

export function ProductCard(props: ProductCardProps) {
  const { t, i18n } = useTranslation()
  // const [searchParams] = useSearchParams()
  // const seasons = searchParams.get('seasons')?.split(',') || []
  const currentLanguage = i18n.language.split('-')[0] as Language

  const { viewMode, product, customView } = props

  const {
    showPicture,
    showDmiSeg,
    // showWholeSaleSeg,
    showPrice,
    showCustomerReview,
    // showModelVariations,
    showBrand,
    showSizeGrid,
    showGender,
    showProductNature,
    showComposition,
    showEcodesign,
    showBenefits,
    showTeaser,
    showDesignedIn,
    showDistributionCustomsZones,
    showProducedIn,
    showHomologatedIn,
    showAvailability,
    showValidationStatus,
    // showMoq,
    showManufacture,
    showSmCompo,
    showModelCompo,
    // showFigures,
    // visibleFigures,
    hasContentBelowBasicInfo,
    span,
    row,
    dividerRow,
    lastSection,
  } = computeProductCardVisibility(viewMode, customView, [])
  // Last parameter is empty ([]) because product.figures is not available for the moment

  const {
    availability_date,
    design_center,
    dmi_segmentation,
    image,
    manufacture,
    selected_customs_zone,
    product_benefits,
    super_model,
    web_catch_line,
    eco_designed,
    co2_environmental_impact,
    // wholesale_segmentation,
  } = product

  const generateAriaLabel = () => {
    const isSm = viewMode === 'sm'
    const productLabel = isSm ? super_model.labels[currentLanguage] : product.model.labels[currentLanguage]
    const productId = isSm ? super_model.id : product.model.id
    return t('aria-label.product-card-title', { type: isSm ? 'sm' : 'model', productLabel, productId: productId.toString().split('').join(' ') })
  }

  return (
    <article
      className={clsx('product-card-container', {
        'product-card-container--model': viewMode === 'model',
      })}
      style={{ gridRow: `span ${span}` }}
      data-testid={
        viewMode === 'sm' ? 'product-card_container' : 'model-card_container'
      }
      tabIndex={0}
      aria-label={generateAriaLabel()}
    >
      {showPicture && (
        <div className="product-picture-container" style={{ gridRow: 1 }}>
          {product.is_new && (
            <NewSeasonBadge className="new-season-badge--product-card" />
          )}
          <ProductImage
            checkAvailability
            image={image}
            imageTestId={viewMode === 'sm'
              ? `product-picture_${super_model.id}`
              : `model-card-picture_${product.model.id}`}
            placeholderClassName="img-container product-card-image-placeholder"
            placeholderTestId={viewMode === 'sm'
              ? `product-picture-placeholder_${super_model.id}`
              : `model-card-picture-placeholder_${product.model.id}`}
            width={330}
          />
        </div>
      )}

      {(showDmiSeg /* || showWholeSaleSeg */) && (
        <div
          className="product-card-segmentation"
          style={{ gridRow: 2 }}
          data-testid={
            viewMode === 'sm' ? 'product-card_segmentation' : 'model-card_segmentation'
          }
        >
          {showDmiSeg && (
            <span className="segmentation-item">
              <DataFallback
                value={dmi_segmentation?.technicity_level}
                fallbackKey="products-page.product-card.no-segmentation"
              >
                {(technicityLevel) => {
                  const level = technicityLevel.replace('_', ' ')
                  return (
                    <>
                      <span className="sr-only">
                        {t('products-page.filters.groups.dmi_segmentations')}
                        {level}
                      </span>
                      <span aria-hidden="true">{level}</span>
                    </>
                  )
                }}
              </DataFallback>
            </span>
          )}
          {/* {showWholeSaleSeg && <span className="segmentation-item">{wholesale_segmentation}</span>} */}
        </div>
      )}

      <div style={{ gridRow: 3 }}>
        {viewMode === 'sm'
          ? (
              <ProductBasicInfo productSM={product} showPrice={showPrice} showCustomerReview={showCustomerReview} />
            )
          : (
              <ModelBasicInfo productModel={product} showPrice={showPrice} showCustomerReview={showCustomerReview} />
            )}
      </div>

      {hasContentBelowBasicInfo && (
        <div className="product-card-divider-row" style={{ gridRow: 4 }}>
          <VpDivider />
        </div>
      )}

      {showSmCompo && (
        <>
          <div style={{ gridRow: row.compo }}>
            <ProductCompoVariations
              productSM={
                product as Extract<typeof props, { viewMode: 'sm' }>['product']
              }
              // showColors={showModelVariations}
              showBrand={showBrand}
              showGender={showGender}
              showProductNature={showProductNature}
              showComposition={showComposition}
            />
          </div>
          {lastSection !== 'compo' && (
            <div className="product-card-divider-row" style={{ gridRow: dividerRow.compo }}>
              <VpDivider />
            </div>
          )}
        </>
      )}

      {showModelCompo && (
        <>
          <div style={{ gridRow: row.compo }}>
            <ModelCompoVariations
              productModel={
                product as Extract<
                  typeof props,
                  { viewMode: 'model' }
                >['product']
              }
              showBrand={showBrand}
              showSizes={showSizeGrid}
              showGender={showGender}
              showProductNature={showProductNature}
              showComposition={showComposition}
              showValidationStatus={showValidationStatus}
            />
          </div>
          {lastSection !== 'compo' && (
            <div className="product-card-divider-row" style={{ gridRow: dividerRow.compo }}>
              <VpDivider />
            </div>
          )}
        </>
      )}

      {showEcodesign && (
        <>
          <div style={{ gridRow: row.ecodesign }}>
            <ProductSustainability
              eco_designed={eco_designed}
              co2_environmental_impact={co2_environmental_impact}
            />
          </div>
          {lastSection !== 'ecodesign' && (
            <div className="product-card-divider-row" style={{ gridRow: dividerRow.ecodesign }}>
              <VpDivider />
            </div>
          )}
        </>
      )}

      {showBenefits && (
        <>
          <div style={{ gridRow: row.benefits }}>
            <ProductBenefits benefits={product_benefits} />
          </div>
          {lastSection !== 'benefits' && (
            <div className="product-card-divider-row" style={{ gridRow: dividerRow.benefits }}>
              <VpDivider />
            </div>
          )}
        </>
      )}

      {showTeaser && (
        <>
          <div
            className="product-card-teaser"
            style={{ gridRow: row.teaser }}
            data-testid={
              viewMode === 'sm' ? 'product-card_teaser' : 'model-card_teaser'
            }
          >
            <span>
              {t('products-page.product-card.spid-teaser')}
            </span>
            <DataFallback
              value={web_catch_line}
              fallbackKey="products-page.product-card.no-teaser"
            >
              {teaser => teaser}
            </DataFallback>
          </div>
          {lastSection !== 'teaser' && (
            <div className="product-card-divider-row" style={{ gridRow: dividerRow.teaser }}>
              <VpDivider />
            </div>
          )}
        </>
      )}

      {showAvailability && (
        <>
          <div style={{ gridRow: row.availability }}>
            <ProductAvailability availabilityDate={availability_date} />
          </div>
          {lastSection !== 'availability' && (
            <div className="product-card-divider-row" style={{ gridRow: dividerRow.availability }}>
              <VpDivider />
            </div>
          )}
        </>
      )}

      {showManufacture && (
        <>
          <div style={{ gridRow: row.manufacture }}>
            <ProductManufacture
              designCenter={design_center}
              manufacture={manufacture}
              showDesignedIn={showDesignedIn}
              selectedCustomsZones={selected_customs_zone}
              showDistributionCustomsZones={showDistributionCustomsZones}
              showProducedIn={showProducedIn}
              showHomologatedIn={showHomologatedIn}
            />
          </div>
          {lastSection !== 'manufacture' && (
            <div className="product-card-divider-row" style={{ gridRow: dividerRow.manufacture }}>
              <VpDivider />
            </div>
          )}
        </>
      )}

      {/* {showFigures && (
        <>
          <div style={{ gridRow: row.figures }}>
            <ProductFigures figures={visibleFigures} />
          </div>
          {lastSection !== 'figures' && (
            <div className="product-card-divider-row" style={{ gridRow: dividerRow.figures }}>
              <VpDivider />
            </div>
          )}
        </>
      )} */}

      {/* {showMoq && (
        <div
          className="product-card-moq"
          style={{ gridRow: row.moq }}
          data-testid={
            viewMode === 'sm' ? 'product-card_moq' : 'model-card_moq'
          }
        >
          <VpIcon name="shopping-add-to-bag" />
          <span>{t('products-page.product-card.moq')}</span>
          {moq.toLocaleString(currentLanguage)}
        </div>
      )} */}
    </article>
  )
}
